import React, { useEffect, useState, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import { getFullPath } from '../../utils/pathUtils';

const serverHost = process.env.REACT_APP_BACKEND_HOST || 'http://localhost:4000';
const exchangeTokenUrl = `${serverHost}/api/auth/twitter/exchange-token`;

interface AuthData {
  profileId: string;
  authCode: string;
  state: string;
  codeVerifier: string;
}

interface ExchangeTokenResponse {
  profileId?: string;
  message?: string;
}

const TwitterCallback: React.FC = () => {
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState<string>('Processing Twitter authorization...');
  const [authData, setAuthData] = useState<AuthData | null>(null);
  const hasProcessed = useRef<boolean>(false);

  useEffect(() => {
    const handleCallback = async () => {
      // Prevent double execution (React StrictMode in development)
      if (hasProcessed.current) {
        console.log('🔄 Callback already processed, skipping...');
        return;
      }
      hasProcessed.current = true;

      console.log('calling handleCallback');
      try {
        // Get authorization code and state from URL
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const state = urlParams.get('state');
        const error = urlParams.get('error');

        if (error) {
          throw new Error(`Authorization failed: ${error}`);
        }

        if (!code || !state) {
          throw new Error('Missing authorization code or state parameter');
        }

        // Get stored PKCE parameters
        const codeVerifier = sessionStorage.getItem('twitter_code_verifier');
        const profileId = sessionStorage.getItem('twitter_profile_id');

        if (!codeVerifier || !profileId) {
          throw new Error('Missing OAuth session data. Please restart the authorization process.');
        }

        // Verify state parameter
        if (!state.startsWith(`profile_${profileId}_`)) {
          throw new Error('Invalid state parameter. Possible CSRF attack.');
        }

        console.log('✅ Twitter OAuth Authorization Successful!');

        // Store the auth data for display
        setAuthData({
          profileId,
          authCode: code,
          state,
          codeVerifier,
        });

        // Send the authorization data to parent window
        if (window.opener) {
          window.opener.postMessage(
            {
              type: 'TWITTER_AUTH_SUCCESS',
              code: code,
              state: state,
              profileId: profileId,
              codeVerifier: codeVerifier,
            },
            window.location.origin
          );
        }

        setStatus('success');
        setMessage('Authorization successful! Exchanging code for tokens...');

        // Exchange code for tokens via backend
        try {
          await exchangeTokensViaBackend(code, codeVerifier, profileId);
        } catch (tokenError) {
          console.error('Token exchange failed:', tokenError);
          setStatus('error');
          const errorMessage = tokenError instanceof Error ? tokenError.message : 'Unknown error';
          setMessage(`Token exchange failed: ${errorMessage}`);
        }

        // Clean up session storage
        sessionStorage.removeItem('twitter_code_verifier');
        sessionStorage.removeItem('twitter_profile_id');

        // Close the popup window after a delay
        setTimeout(() => {
          if (window.opener) {
            window.close();
          } else {
            // If not in a popup, redirect back to the main app
            window.location.href = getFullPath('/settings/accounts');
          }
        }, 8000); // Longer delay to read the information
      } catch (error) {
        console.error('Error in Twitter OAuth callback:', error);
        setStatus('error');
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        setMessage(`Authorization failed: ${errorMessage}`);

        // Close popup after showing error
        setTimeout(() => {
          if (window.opener) {
            window.close();
          }
        }, 5000);
      }
    };

    const exchangeTokensViaBackend = async (
      authCode: string,
      codeVerifier: string,
      profileId: string
    ): Promise<ExchangeTokenResponse> => {
      console.log('🔄 Exchanging tokens via backend...');

      const response = await fetch(exchangeTokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code: authCode,
          codeVerifier: codeVerifier,
          profileId: profileId,
        }),
      });

      if (!response.ok) {
        const errorData: ExchangeTokenResponse = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Backend request failed: ${response.status}`);
      }

      const result: ExchangeTokenResponse = await response.json();

      console.log('✅ Backend token exchange successful!');

      setStatus('success');
      setMessage('Twitter authorization and token exchange completed successfully!');

      return result;
    };

    handleCallback();
  }, []);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-6 text-center max-w-2xl mx-auto">
      {status === 'processing' && (
        <>
          <Loader2 className="w-9 h-9 mb-4 text-primary animate-spin" />
          <h6 className="text-xl font-semibold text-white mb-2">Processing Authorization</h6>
        </>
      )}

      {status === 'success' && (
        <>
          <h6 className="text-xl font-semibold text-success mb-4">
            ✅ Authorization Code Received!
          </h6>

          <div className="mb-6 w-full p-4 bg-info/20 border border-info rounded">
            <p className="text-sm text-white">The authorization was successful!</p>
          </div>

          {authData && (
            <div className="p-4 w-full text-left font-mono text-sm bg-core-dark rounded shadow border border-cmp-line-2">
              <div>
                <strong className="text-white">Profile ID:</strong>{' '}
                <span className="text-cmp-ink">{authData.profileId}</span>
                <br />
              </div>
            </div>
          )}
        </>
      )}

      {status === 'error' && (
        <>
          <h6 className="text-xl font-semibold text-error mb-2">❌ Authorization Failed</h6>
        </>
      )}

      <p className="mt-4 text-cmp-ink-2">{message}</p>

      {status !== 'processing' && (
        <p className="mt-4 text-sm text-cmp-ink-3">This window will close automatically...</p>
      )}
    </div>
  );
};

export default TwitterCallback;
